'use client'

import { createContext, useEffect, useReducer, useState } from 'react'
import { reducer } from './reducer'
import initialState from './state'
import { PageState } from './types'
import { SET_PAGE } from './actions'
import { noop } from 'lodash'
import { Logger } from '@wolfdanepug/common-react'
import { useLocation, useNavigate } from 'react-router'

export type PageContextType = {
	usePageState: () => PageState
	setPage: (payload: any) => void
	setHandlers: (handlers: any) => void
	setPageData: (data: any) => void
	onPageAction: (action: string, data: any) => void
	usePageData: (noData?: any) => any
	setResponseHandlers: (handlers: any) => void
}

export type PageReducer = (state: PageState, { type, payload }: any) => PageState

export const PageContext = createContext<PageContextType>({
	usePageState: () => Object.freeze(initialState),
	setPage: (payload: any) => {},
	setHandlers: (handlers: any) => {},
	setPageData: (data: any) => {},
	onPageAction: (action: string, data: any) => {},
	usePageData: (noData: any) => ({}),
	setResponseHandlers: (handlers: any) => {}
})

export const PageProvider = ({ children, onChange = noop }: any) => {
	const [state, dispatch] = useReducer<PageReducer>(reducer, initialState)
	const [pageData, setPageData] = useState<any>(null)
	const [handlers, setHandlers] = useState<any>({})
	const [responseHandlers, setResponseHandlers] = useState<any>({})

	const navigate = useNavigate()
	const { pathname } = useLocation()

	useEffect(() => {
		setPageData(null)
	}, [pathname])

	const usePageState = () => {
		return state
	}

	const usePageData = () => {
		return pageData || {}
	}

	const handleResponse = async (action: string, response: any) => {
		const responseHandler = responseHandlers[action]
		if (responseHandler) {
			const path = await responseHandler(response)
			if (typeof path !== 'string') {
				Logger.error('Response handler must return a string')
			} else {
				navigate(path)
			}
		}
	}

	const handlePageAction = async (action: string, data: any) => {
		onChange({ loading: true })
		const handler = handlers[action]
		if (!handler) {
			Logger.error(`Handler for action ${action} not found`)
			onChange({ loading: false })
			return
		}
		try {
			const response = await handler(data || pageData)
			Logger.log('[PageProvider] handlePageAction.response', JSON.stringify(response, null, 2))
			onChange({ loading: false })
			handleResponse(action, response)
		} catch (e) {
			Logger.error(e)
			onChange({ loading: false })
		}
	}

	return (
		<PageContext.Provider
			value={{
				usePageState,
				setPage: (payload: any) => dispatch({ type: SET_PAGE, payload }),
				setHandlers,
				setPageData,
				onPageAction: handlePageAction,
				usePageData,
				setResponseHandlers
			}}
		>
			{children}
		</PageContext.Provider>
	)
}
